import type { TruckTrailerCoupling } from '@chukua/common';
import { Table, Tag, Typography, type TableColumnsType } from 'antd';
import { Link2Off, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../shared/components/TableSkeleton';
import { unitPath } from '../features/units/units.paths';
import { useCouplingHistory } from '../hooks/use-coupling-history';
import { dropPlaceLabel, formatCouplingTime } from '../policies/coupling-labels';

function columns(vehicleKind: 'truck' | 'trailer', sortOrder: 'ascend' | 'descend' | null): TableColumnsType<TruckTrailerCoupling> {
  const other = vehicleKind === 'truck' ? 'trailer' : 'truck';
  return [
    {
      title: other === 'trailer' ? 'Trailer' : 'Truck',
      key: other,
      render: (_, row) => {
        const registration = other === 'trailer' ? row.trailerRegistration : row.truckRegistration;
        return (
          <Link className="text-link hover:underline" to={unitPath(registration)}>
            <Typography.Text code>{registration}</Typography.Text>
          </Link>
        );
      },
    },
    { title: 'From', dataIndex: 'coupledAt', key: 'coupledAt', sorter: true, sortOrder, render: formatCouplingTime },
    { title: 'To', key: 'uncoupledAt', render: (_, row) => (row.uncoupledAt ? formatCouplingTime(row.uncoupledAt) : <Tag color="success">Current</Tag>) },
    { title: 'Dropped at', key: 'dropZone', render: (_, row) => dropPlaceLabel(row) },
    { title: 'By', key: 'by', render: (_, row) => row.uncoupledByName ?? row.coupledByName ?? '—' },
  ];
}

/** Which trailer a truck pulled, or which trucks pulled a trailer, and when (gitaumoses4/chukua#390). */
export function CouplingHistoryTable({ vehicleKind, vehicleId }: { vehicleKind: 'truck' | 'trailer'; vehicleId: string }) {
  const { state, list, isRefreshing } = useCouplingHistory(vehicleKind, vehicleId);

  if (state.status === 'loading') return <TableSkeleton label="Loading coupling history" />;
  if (state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the coupling history" description="Something went wrong. Try reloading the page." />;
  }
  if (state.page.total === 0) {
    const description = vehicleKind === 'truck' ? 'This truck has never pulled a trailer here.' : 'No truck has pulled this trailer yet.';
    return <EmptyState icon={Link2Off} title="Never coupled" description={description} />;
  }
  return (
    <Table
      rowKey="id"
      dataSource={state.page.items}
      columns={columns(vehicleKind, list.sortOrderFor('coupledAt') ?? null)}
      loading={isRefreshing}
      scroll={{ x: 'max-content' }}
      {...list.tableProps<TruckTrailerCoupling>(state.page.total)}
    />
  );
}
